import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  FiMenu,
  FiX,
  FiUser,
  FiLogOut,
  FiShield,
  FiAlertCircle,
} from "react-icons/fi";
import Button from "../common/Button";
import {
  clearAuth,
  clearSessionExpiredMessage,
  getSessionExpiredMessage,
  getStoredRole,
  hasValidSession,
} from "../../services/authService";

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("");
  const navigate = useNavigate();

  const isAuthenticated = hasValidSession();
  const role = getStoredRole();
  const isAdmin = isAuthenticated && role === "ADMIN";

  useEffect(() => {
    const message = getSessionExpiredMessage();

    if (message) {
      setSessionExpiredMessage(message);
      clearSessionExpiredMessage();
    }
  }, []);

  const navLinks = useMemo(
    () => [
      { to: "/", label: "Home", end: true },
      { to: "/services", label: "Services" },
      { to: "/booking", label: "Book" },
      { to: "/gallery", label: "Gallery" },
      { to: "/contact", label: "Contact" },
    ],
    []
  );

  const linkClass = ({ isActive }) =>
    [
      "transition-colors duration-200 text-sm font-medium",
      isActive ? "text-henna" : "text-[#3b2f2f] hover:text-henna",
    ].join(" ");

  const mobileLinkClass = ({ isActive }) =>
    [
      "block rounded-xl px-4 py-3 text-sm font-medium transition-colors duration-200",
      isActive
        ? "bg-soft-cream text-henna"
        : "text-[#3b2f2f] hover:bg-soft-cream hover:text-henna",
    ].join(" ");

  const handleLogout = () => {
    clearAuth();
    setMobileOpen(false);
    navigate("/", { replace: true });
  };

  const closeMobileMenu = () => setMobileOpen(false);

  return (
    <header className="sticky top-0 z-40 border-b border-goldlight bg-white/90 backdrop-blur">
      <div className="container-page">
        <div className="flex min-h-[72px] items-center justify-between gap-4">
          <NavLink
            to="/"
            onClick={closeMobileMenu}
            className="shrink-0 text-2xl font-semibold tracking-tight text-henna"
          >
            Nayra Henna
          </NavLink>

          <nav className="hidden items-center gap-6 lg:flex">
            {navLinks.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={linkClass}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            {isAuthenticated ? (
              <>
                <NavLink to="/profile">
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<FiUser />}
                  >
                    My Account
                  </Button>
                </NavLink>

                {isAdmin && (
                  <NavLink to="/admin">
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<FiShield />}
                    >
                      Admin Dashboard
                    </Button>
                  </NavLink>
                )}

                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<FiLogOut />}
                  onClick={handleLogout}
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <NavLink to="/login">
                  <Button variant="secondary" size="sm">
                    Sign In
                  </Button>
                </NavLink>

                <NavLink to="/register">
                  <Button variant="primary" size="sm">
                    Create Account
                  </Button>
                </NavLink>
              </>
            )}
          </div>

          <button
            type="button"
            className="inline-flex items-center justify-center rounded-xl border border-goldlight p-2 text-[#3b2f2f] transition hover:bg-soft-cream hover:text-henna lg:hidden"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label="Toggle navigation menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </button>
        </div>

        {sessionExpiredMessage ? (
          <div className="pb-4">
            <div className="flex items-start justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 text-amber-600">
                  <FiAlertCircle size={18} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-800">
                    Your session has ended
                  </p>
                  <p className="mt-1 text-sm text-amber-700">
                    {sessionExpiredMessage}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSessionExpiredMessage("")}
                className="text-sm font-medium text-amber-700 transition hover:text-amber-900"
              >
                Dismiss
              </button>
            </div>
          </div>
        ) : null}

        {mobileOpen && (
          <div className="border-t border-goldlight py-4 lg:hidden">
            <div className="space-y-2">
              {navLinks.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={mobileLinkClass}
                  onClick={closeMobileMenu}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>

            <div className="mt-4 border-t border-goldlight pt-4">
              {isAuthenticated ? (
                <div className="space-y-3">
                  <NavLink
                    to="/profile"
                    className="block"
                    onClick={closeMobileMenu}
                  >
                    <Button
                      fullWidth
                      variant="secondary"
                      size="sm"
                      leftIcon={<FiUser />}
                    >
                      My Account
                    </Button>
                  </NavLink>

                  {isAdmin && (
                    <NavLink
                      to="/admin"
                      className="block"
                      onClick={closeMobileMenu}
                    >
                      <Button
                        fullWidth
                        variant="outline"
                        size="sm"
                        leftIcon={<FiShield />}
                      >
                        Admin Dashboard
                      </Button>
                    </NavLink>
                  )}

                  <Button
                    fullWidth
                    variant="primary"
                    size="sm"
                    leftIcon={<FiLogOut />}
                    onClick={handleLogout}
                  >
                    Sign Out
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <NavLink
                    to="/login"
                    className="block"
                    onClick={closeMobileMenu}
                  >
                    <Button fullWidth variant="secondary" size="sm">
                      Sign In
                    </Button>
                  </NavLink>

                  <NavLink
                    to="/register"
                    className="block"
                    onClick={closeMobileMenu}
                  >
                    <Button fullWidth variant="primary" size="sm">
                      Create Account
                    </Button>
                  </NavLink>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;